import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Target, Clock, Zap, Gift, ChevronRight, Star, Shield } from "lucide-react";
import api from "@/lib/api";
import { fmtDateTime, euro } from "@/lib/format";
import TeamCrest from "@/components/TeamCrest";
import { useApp } from "@/context/AppContext";

const STADIUM = "https://images.unsplash.com/photo-1568101794887-a7a3f149f6e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxzdGFkaXVtJTIwbmlnaHQlMjBmbG9vZGxpZ2h0cyUyMGZvb3RiYWxsJTIwcGl0Y2h8ZW58MHx8fGJsYWNrfDE3OTE4OTE1NDJ8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { settings } = useApp();
  const [matches, setMatches] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const price = settings?.subscription_price || 4.99;

  useEffect(() => {
    api.get("/matches", { params: { status: "scheduled" } }).then(({ data }) => setMatches(data.slice(0, 4))).catch(() => {});
    api.get("/prizes").then(({ data }) => setPrizes(data.current_prizes || [])).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <img src={STADIUM} alt="Stadio" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/85 to-ink-900/55" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-900/90 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 w-full">
          <div className="max-w-2xl animate-fade-up">
            <div className="overline-label mb-4 flex items-center gap-2"><Star size={14} className="fill-gold-300" /> Serie A · UEFA Champions League</div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tight leading-[0.95] text-white">
              Pronostica.<br />Scala la <span className="gold-gradient-text">Classifica</span>.<br />Vinci.
            </h1>
            <p className="text-lg text-slate-300 mt-6 max-w-xl">
              La Challenge ufficiale di <b className="text-white">The King of Resell</b>. Pronostica 1-X-2 sulle partite di Serie A e Champions League, accumula punti e vinci scarpe da calcio e sneaker esclusive <b className="text-gold-300">ogni 30 giorni</b>.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Link to="/abbonamento" data-testid="hero-participate-btn" className="btn-gold text-base !py-4 !px-8">
                <Zap size={20} /> PARTECIPA ALLA CHALLENGE
              </Link>
              <Link to="/classifica" data-testid="hero-leaderboard-btn" className="btn-outline-gold text-base !py-4 !px-8">
                <Trophy size={20} /> VEDI LA CLASSIFICA
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 text-sm text-slate-400">
              <span className="flex items-center gap-1.5"><Shield size={16} className="text-gold-300" /> {euro(price)}/mese</span>
              <span className="flex items-center gap-1.5"><Target size={16} className="text-gold-300" /> +3 punti a esito</span>
              <span className="flex items-center gap-1.5"><Clock size={16} className="text-gold-300" /> Chiusura 15 min prima</span>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Target, t: "Sistema 1-X-2", d: "3 punti per ogni esito corretto. Nessuna penalità per gli errori." },
            { icon: Clock, t: "Blocco 15 min", d: "I pronostici si chiudono esattamente 15 minuti prima del fischio d'inizio." },
            { icon: Trophy, t: "Classifica Live", d: "Aggiornata automaticamente dopo la conferma di ogni risultato ufficiale." },
            { icon: Gift, t: "Premi ogni 30 giorni", d: "Il podio vince scarpe da calcio, sneaker e kit esclusivi." },
          ].map((b, i) => (
            <div key={i} className="surface p-6 hover:border-gold-300/40 transition-colors">
              <div className="grid place-items-center h-12 w-12 rounded-full bg-gold-300/10 text-gold-300 mb-4"><b.icon size={24} /></div>
              <h3 className="font-heading text-xl font-bold uppercase text-white">{b.t}</h3>
              <p className="text-sm text-muted-foreground mt-2">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* UPCOMING */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="overline-label mb-1">Prossime partite</div>
            <h2 className="font-heading text-3xl font-extrabold uppercase text-white">In Programma</h2>
          </div>
          <Link to="/calendario" className="text-gold-300 text-sm flex items-center gap-1 hover:text-gold-200 transition-colors">Calendario <ChevronRight size={16} /></Link>
        </div>
        {matches.length === 0 ? (
          <div className="surface p-8 text-center text-muted-foreground">Nessuna partita in programma al momento.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {matches.map((m) => (
              <Link to="/calendario" key={m.id} className="surface p-4 flex items-center justify-between hover:border-gold-300/40 transition-colors">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <TeamCrest name={m.home_team} short={m.home_short} logo={m.home_logo} size={40} />
                  <span className="font-semibold text-white text-sm truncate">{m.home_team}</span>
                </div>
                <div className="text-center px-3">
                  <div className="text-[0.65rem] overline-label !tracking-wide">{m.competition_code === "UCL" ? "UCL" : "Serie A"}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{fmtDateTime(m.kickoff)}</div>
                </div>
                <div className="flex items-center gap-3 flex-1 min-w-0 justify-end">
                  <span className="font-semibold text-white text-sm truncate text-right">{m.away_team}</span>
                  <TeamCrest name={m.away_team} short={m.away_short} logo={m.away_logo} size={40} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* PRIZES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center mb-10">
          <div className="overline-label mb-2">Montepremi del periodo</div>
          <h2 className="font-heading text-4xl font-black uppercase text-white">Cosa puoi <span className="gold-gradient-text">Vincere</span></h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {(prizes.length ? prizes : [
            { position: 1, title: "1° Classificato", description: "Scarpa da calcio + Sneaker esclusiva" },
            { position: 2, title: "2° Classificato", description: "Scarpa da calcio premium" },
            { position: 3, title: "3° Classificato", description: "Pallone ufficiale o kit maglia + pantaloncini" },
          ]).map((p) => (
            <div key={p.position} className={`surface overflow-hidden ${p.position === 1 ? "md:-mt-4 border-gold-300/40 shadow-gold" : ""}`}>
              {p.image_url && <img src={p.image_url} alt={p.title} className="w-full h-44 object-cover" />}
              <div className="p-6">
                <div className={`font-heading text-5xl font-black ${p.position === 1 ? "gold-gradient-text" : "text-ink-600"}`}>{p.position}°</div>
                <h3 className="font-heading text-xl font-bold uppercase text-white mt-2">{p.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{p.description}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/abbonamento" className="btn-gold text-base !py-4 !px-8"><Zap size={20} /> PARTECIPA ALLA CHALLENGE</Link>
        </div>
      </section>
    </div>
  );
}
